/**
 * The failures the API layer reports.
 *
 * They live in their own module because the offline layer raises two of them and the HTTP
 * client raises the third, and neither of those two modules may import the other.
 */

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    if (code !== undefined) this.code = code;
  }
}

/** The code every write accepted onto the device rather than sent to the server carries. */
export const OFFLINE_QUEUED_CODE = 'offline.queued';

/** The code every request that cannot be answered without a connection carries. */
export const OFFLINE_UNAVAILABLE_CODE = 'offline.unavailable';

/**
 * Not a failure — an acknowledgement.
 *
 * A write captured on the device is deliberately reported as a rejected promise rather than as a
 * successful one carrying an invented server response. Nothing has been numbered, nothing has
 * been validated by the API and nothing has an identifier yet, so handing back a document that
 * looks saved would be a lie the rest of the workspace would then act on. The status is `202`
 * because that is exactly what happened: accepted, not yet applied.
 */
export class OfflineQueuedError extends ApiRequestError {
  readonly queueId: string;

  constructor(queueId: string) {
    super(202, 'Varza saved this on the device and will send it when the connection returns.', OFFLINE_QUEUED_CODE);
    this.name = 'OfflineQueuedError';
    this.queueId = queueId;
  }
}

/** Raised when there is no connection and no offline answer for the request. */
export class OfflineUnavailableError extends ApiRequestError {
  constructor(message = 'This needs a connection to the Varza server.') {
    super(503, message, OFFLINE_UNAVAILABLE_CODE);
    this.name = 'OfflineUnavailableError';
  }
}

export function isOfflineQueued(error: unknown): error is OfflineQueuedError {
  return error instanceof ApiRequestError && error.code === OFFLINE_QUEUED_CODE;
}

export function isOfflineUnavailable(error: unknown): error is OfflineUnavailableError {
  return error instanceof ApiRequestError && error.code === OFFLINE_UNAVAILABLE_CODE;
}

/**
 * True for the failure `fetch` reports when the request never reached a server: no network, DNS
 * failure, a refused connection, a captive portal. It is the one error that says nothing about
 * what the server did, which is why it is the only one the sync engine treats as "try again".
 */
export function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof DOMException && error.name === 'NetworkError');
}
